import Vue from 'vue'
import VueRouter from 'vue-router'
import MainLayout from '@/layouts/MainLayout.vue'
import store from '@/store'
import { ROUTE_PERM_MAP, PUBLIC_ROUTES } from '@/config/menuPermissions'
import { MENU_GROUPS } from '@/config/menuStructure'
import { getUserPermissionByToken, getDryingMenuByToken } from '@/api/auth'
import { tabsStore } from '@/store/tabsView'

// T-017: 共享 placeholder 组件 — 思维导图里规划但尚未开发的菜单都用它占位
const Placeholder = () => import('@/views/Placeholder.vue')

// 已存在的视图组件 (key → 动态 import). 菜单里的 key 在这里有映射就用对应组件, 没映射就走 Placeholder.vue
const EXISTING_VIEWS = {
  'entry-v1': () => import('@/views/EntryV1.vue'),
  // [2026-09-08] 粮食手工录入 — 与 /entry-v1 共用 EntryV1 组件 (keep-alive 同实例),
  //   EntryV1 按路由路径自动切到「手工录入」tab (过磅作业/手工录入/入库单管理/入库退货 四 tab 复用同一容器)
  'manual-entry': () => import('@/views/EntryV1.vue'),
  exit: () => import('@/views/Exit.vue'),
  // [2026-05-30] 单据核验 (财务结算前查小票真伪)
  'receipt-verify': () => import('@/views/ReceiptVerify.vue'),
  // 客商管理 → Customer.vue (本厂自建 + 粮联同步 联合视图, 含 OCR 营业执照识别)
  customer: () => import('@/views/Customer.vue'),
  formula: () => import('@/views/Formula.vue'),
  'quality-price': () => import('@/views/QualityPrice.vue'),
  user: () => import('@/views/UserMgmt.vue'),
  role: () => import('@/views/RoleMgmt.vue'),
  dict: () => import('@/views/DictMgmt.vue'),
  // T-020 legacy: 6 个原型页, 不在思维导图里但保留为菜单 (有 legacy 标记)
  seed: () => import('@/views/Seed.vue'),
  // [2026-06-04] 农资入库 (种子/农药/化肥) — 原型阶段
  'seed-in': () => import('@/views/SeedIn.vue'),
  // [T-101 2026-06-07] 农资出库 — 镜像 SeedIn (库存扣减 + 应收递增)
  'seed-out': () => import('@/views/SeedOut.vue'),
  farmer: () => import('@/views/Farmer.vue'),
  account: () => import('@/views/Account.vue'),
  payment: () => import('@/views/Payment.vue'),
  // [T-172] 以粮抵种 — 客户维度批量抵扣页 (填金额, 后端先欠先抵自动分配, 支持部分抵扣)
  'grain-for-seed': () => import('@/views/GrainForSeed.vue'),
  report: () => import('@/views/Report.vue'),
  // [2026-07-23 报表重构] 8 张统计表 (完全替代旧报表中心)
  //   资金结算表复用 AccountFlow; 其余 7 张走聚合报表后端 (/drying/aggr-report/**)
  // [2026-07-23 v2] 采购/销售合并为汇总表(维度切换+下钻+柱状图); 原4页保留直达(指向汇总表)
  'report-settlement':           () => import('@/views/aggr-report/ReportSettlement.vue'),
  'report-purchase-summary':     () => import('@/views/aggr-report/PurchaseSummary.vue'),
  'report-sales-summary':        () => import('@/views/aggr-report/SalesSummary.vue'),
  'report-purchase-by-variety':  () => import('@/views/aggr-report/PurchaseSummary.vue'),
  'report-purchase-by-customer': () => import('@/views/aggr-report/PurchaseSummary.vue'),
  'report-sales-by-variety':     () => import('@/views/aggr-report/SalesSummary.vue'),
  'report-sales-by-customer':    () => import('@/views/aggr-report/SalesSummary.vue'),
  'report-psi':                  () => import('@/views/aggr-report/PsiSummary.vue'),
  'report-stock':                () => import('@/views/aggr-report/StockSummary.vue'),
  'report-moisture':             () => import('@/views/aggr-report/MoistureReport.vue'),
  'report-order-recovery':       () => import('@/views/aggr-report/OrderRecovery.vue'),
  'report-input-purchase':       () => import('@/views/aggr-report/InputPurchaseSummary.vue'),
  'report-input-sales':          () => import('@/views/aggr-report/InputSalesSummary.vue'),
  // [2026-10-10 理论干粮] 报表 #10 折干对账表 (复用占位权限 DRY_RPT_RECONCILE, T201 已绑三角色)
  'report-dry-reconcile':        () => import('@/views/aggr-report/DryReconcile.vue'),
  // [T-130] 4 张报表 — 元数据驱动通用 ReportPage (旧, 已隐藏)
  'report-grain-in':  () => import('@/views/report/GrainInReport.vue'),
  'report-grain-out': () => import('@/views/report/GrainOutReport.vue'),
  'report-input-in':  () => import('@/views/report/InputInReport.vue'),
  'report-input-out': () => import('@/views/report/InputOutReport.vue'),
  settings: () => import('@/views/Settings.vue'),
  // T-021 仓库管理 4 个新原型页
  'initial-stock': () => import('@/views/InitialStock.vue'),
  'wh-input':      () => import('@/views/WarehouseInput.vue'),
  'wh-grain':      () => import('@/views/WarehouseGrain.vue'),
  'stock-count':   () => import('@/views/StockCount.vue'),
  // [T-180] 全局库存流水查询
  'stock-log-global': () => import('@/views/StockLogGlobal.vue'),
  // T-023 商品档案 (粮食 + 农资共用同一个页面)
  product: () => import('@/views/Product.vue'),
  // T-054 司机与车辆档案
  driver: () => import('@/views/Driver.vue'),
  // T-054C 出库相关财务: 收款管理 + 应收台账 (财务模块菜单)
  collect: () => import('@/views/Collect.vue'),
  'ar-account': () => import('@/views/ArAccount.vue'),
  // T-061 资金结算表 (财务模块菜单) — 原"辅助结算表"
  'account-flow': () => import('@/views/AccountFlow.vue'),
  // [T-221 反向开票] 付款申请 (库管发起 → 财务审核 → 执行)
  'payment-apply': () => import('@/views/PaymentApply.vue'),
  // [T-222 反向开票] 开票管理 (发票状态展示 + 异常监控)
  'invoice-manage': () => import('@/views/InvoiceManage.vue'),
  // [T-232 反向开票] 资质管理 (厂超管提交申请 → 平台超管审核)
  'qual-apply': () => import('@/views/QualApply.vue'),
  // [反向开票] 农户供应商管理 (登记农户为支付宝供应商 + 刷新确认状态)
  'farmer-supplier': () => import('@/views/FarmerSupplier.vue'),
  // [反向开票] 营业员管理 (支付宝 companyclerk 同步/创建/设默认)
  'company-clerk': () => import('@/views/CompanyClerk.vue'),
  // [反向开票] 商品管理 (厂×品种 → 支付宝 productId 映射)
  'factory-product': () => import('@/views/FactoryProduct.vue'),
  // [反向开票] 发票配置 (产品/账户/营业员/税收编码/启用/熔断)
  'invoice-config': () => import('@/views/InvoiceConfig.vue'),
  // [#5] 风险提示库管理 (平台级维护, 增/改/停用/版本)
  'risk-tip-mgmt': () => import('@/views/RiskTipMgmt.vue'),
  // [#7] 厂-风险绑定配置 (按厂勾选适用风险项)
  'fac-risk-assign': () => import('@/views/FacRiskAssign.vue'),
  // [#8] 风险项批量配置 (按省筛选厂 + 批量增删替换)
  'fac-risk-batch': () => import('@/views/FacRiskBatchAssign.vue'),
  // T-060 操作日志 (系统管理菜单)
  'op-log': () => import('@/views/OpLog.vue'),
  // 过磅设备配置 (地磅↔摄像头绑定, 设备管理菜单; [2026-08-31] 从系统管理移入设备管理分组)
  'weigh-device': () => import('@/views/WeighDeviceConfig.vue'),
  // [2026-08-31 设备管理] 外设调试测试页 — jinglunGo 调试页(127.0.0.1:8765)迁入烘干厂前端,
  //   测试操作直接读写设备配置(localStorage)与 jinglunGo 服务, 调试好即为正式配置
  'device-drivers':        () => import('@/views/device/DeviceDrivers.vue'),
  'device-card-test':      () => import('@/views/device/DeviceCardTest.vue'),
  'device-camera-test':    () => import('@/views/device/DeviceCameraTest.vue'),
  'device-scale-test':     () => import('@/views/device/DeviceScaleTest.vue'),
  'device-triplicate-test': () => import('@/views/device/DeviceTriplicateTest.vue'),
  'device-receipt-test':   () => import('@/views/device/DeviceReceiptTest.vue'),
  // 粮单配置 (入库/出库录磅字段开关, 系统管理菜单)
  'bill-field-config': () => import('@/views/BillFieldConfig.vue'),
  // T-089 跨端联动
  'cross-platform': () => import('@/views/CrossEnd.vue'),
  // [T-151] 外设服务版本管理 (系统管理菜单, 上传/发布/归档 客户端 zip)
  'peripheral-release': () => import('@/views/PeripheralRelease.vue'),
  // T-127 菜单与导航 (系统管理菜单)
  'menu-nav': () => import('@/views/MenuNav.vue'),
  // [T-128] 消息通知管理 (系统管理菜单, perm DRY_SYS_NOTICE_MGMT)
  'notice-mgmt': () => import('@/views/NoticeMgmt.vue'),
  // [模块开关] 烘干厂分配页 (仅 SUPPER_ADMIN 可见, 无 perm → 路由层面靠角色守卫)
  'factory-module-assign': () => import('@/views/FactoryModuleAssign.vue')
}

// 把 menuStructure 里所有 item 转成 router 的 children, 与 EXISTING_VIEWS 匹配
function buildMenuRoutes() {
  const out = []
  MENU_GROUPS.forEach(g => {
    g.items.forEach(it => {
      out.push({
        path: it.key,
        name: it.key,
        component: EXISTING_VIEWS[it.key] || Placeholder,
        // [2026-08-14] superOnly 菜单挂 requireSuperAdmin, 复用「外设服务版本/烘干厂分配」同款角色守卫
        meta: { title: it.label, ...(it.superOnly ? { requireSuperAdmin: true } : {}) }
      })
    })
  })
  return out
}

// 刷新页面时 Vuex 内存丢失 -> 用 token 重新拉一次菜单权限
// 期间用 Promise 并发去重 (多个路由守卫同时触发只跑一次)
let pendingAuthFetch = null
function ensurePermissionsLoaded() {
  if (Array.isArray(store.state.permKeys) && store.state.permKeys.length > 0) {
    return Promise.resolve()
  }
  if (pendingAuthFetch) return pendingAuthFetch
  // 菜单走独立接口异步加载 (登录响应不再携带菜单).
  // getDryingMenuByToken 返回 [{id}] 数组, commit SET_DRYING_MENUS 派生 permKeys.
  pendingAuthFetch = getDryingMenuByToken()
    .then(menus => { store.commit('SET_DRYING_MENUS', menus) })
    .catch(err => {
      // eslint-disable-next-line no-console
      console.warn('[router] getDryingMenuByToken failed', err)
    })
    .finally(() => { pendingAuthFetch = null })
  return pendingAuthFetch
}

Vue.use(VueRouter)

// 静默 Vue Router 3.1+ 所有"导航被打断"的报错 (这些不是真错误, 是 router 把流程控制当 error 抛了):
//   redirected (type=2)  - 守卫里 next(otherPath) 重定向
//   aborted    (type=4)  - 守卫 next(false)
//   cancelled  (type=8)  - 新导航打断旧导航
//   duplicated (type=16) - 重复点同一菜单
// 注意: VueRouter 把这些 error 的 name 字段设为 'NavigationDuplicated' 等是 4.x 行为,
// 3.x 的 name 还是 'Error', 真正标识在 err._isRouter + err.type
function isNavigationFailure(err) {
  if (!err) return false
  // 4.x 兼容: name 形如 'NavigationDuplicated'
  if (typeof err.name === 'string' && /^Navigation/.test(err.name)) return true
  // 3.x: _isRouter 标记 + 数字 type
  if (err._isRouter === true && typeof err.type === 'number') return true
  // 兜底: message 包含特征文字 (最后一道防线)
  if (typeof err.message === 'string' && /Redirected when going from|Navigation aborted|Navigation cancelled|Avoided redundant navigation/i.test(err.message)) return true
  return false
}
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
  return originalPush.call(this, location).catch(err => {
    if (isNavigationFailure(err)) return err
    throw err
  })
}
const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) return originalReplace.call(this, location, onResolve, onReject)
  return originalReplace.call(this, location).catch(err => {
    if (isNavigationFailure(err)) return err
    throw err
  })
}

const routes = [
  {
    path: '/login',
    component: () => import('@/views/Login.vue')
  },
  {
    path: '/',
    component: MainLayout,
    redirect: '/entry-v1',
    children: [
      // 业务/系统菜单 - 由 menuStructure 单一来源生成 (含 T-020 legacy 标记的 6 个原型页)
      ...buildMenuRoutes(),
      // 非菜单页 (不在导航里, 但可路由到)
      { path: 'notice',         name: 'notice',         component: () => import('@/views/Notice.vue'),       meta: { title: '通知中心' } },
      { path: 'profile',        name: 'profile',        component: () => import('@/views/Profile.vue'),      meta: { title: '个人中心' } },
      // 教程中心 (/tutorial 为选择主页) + 各教程子页, 所有登录用户可见, 不走菜单与权限;
      // 新增教程: views/tutorial/ 下建页面 + 这里注册路由 + TutorialHome.vue 卡片配置 + PUBLIC_ROUTES 加 name
      { path: 'tutorial',                name: 'tutorial',                component: () => import('@/views/tutorial/TutorialHome.vue'),          meta: { title: '教程中心' } },
      { path: 'tutorial/haoliangbao',    name: 'tutorial-haoliangbao',    component: () => import('@/views/tutorial/TutorialHaoliangbao.vue'),    meta: { title: '好粮宝使用教程' } },
      { path: 'tutorial/alipay-invoice', name: 'tutorial-alipay-invoice', component: () => import('@/views/tutorial/TutorialAlipayInvoice.vue'), meta: { title: '支付宝反向开票手册' } },
      { path: 'tutorial/formula',        name: 'tutorial-formula',        component: () => import('@/views/tutorial/TutorialFormula.vue'),        meta: { title: '质价公式与干粮换算' } },
      { path: 'tutorial/ai',             name: 'tutorial-ai',             component: () => import('@/views/tutorial/TutorialAi.vue'),             meta: { title: 'AI 智能问答' } },
      // T-028: 期初库存导入 移出菜单 - 作为粮食仓/农资仓的功能入口, 路由保留可直访
      { path: 'initial-stock',  name: 'initial-stock',  component: () => import('@/views/InitialStock.vue'), meta: { title: '期初库存导入' } },
      // [T-078 2026-06-02] 质价公式详情 (从列表跳; 不在菜单)
      { path: 'quality-price/detail', name: 'quality-price-detail',
        component: () => import('@/views/QualityPriceDetail.vue'),
        meta: { title: '质价公式 · 详情' } },
      // [模块开关] 烘干厂分配 (仅 SUPPER_ADMIN 可见, 不走 menuStructure, 由 MainLayout 角色判断显示)
      { path: 'factory-module-assign', name: 'factory-module-assign',
        component: () => import('@/views/FactoryModuleAssign.vue'),
        meta: { title: '烘干厂分配', requireSuperAdmin: true } },
      // [T-151] 外设服务版本 (仅 SUPPER_ADMIN 可见, 不走 menuStructure / perm)
      { path: 'peripheral-release', name: 'peripheral-release',
        component: () => import('@/views/PeripheralRelease.vue'),
        meta: { title: '外设服务版本', requireSuperAdmin: true } },
      // [2026-09-15] 地磅连接健康 (仅 SUPPER_ADMIN 可见, 同外设服务版本模式;
      //   连接事件由烘干厂 PC 前端自动上报, 评估周报用, 导出为 Markdown 摘要)
      { path: 'scale-connect-health', name: 'scale-connect-health',
        component: () => import('@/views/ScaleConnectHealth.vue'),
        meta: { title: '地磅连接健康', requireSuperAdmin: true } }
    ]
  }
]

const router = new VueRouter({
  mode: 'hash',
  routes
})

router.beforeEach(async (to, from, next) => {
  // 海康监控插件是全局原生覆盖层 (不是普通 DOM), 切页面时组件清理常不可靠, 会浮在别的页面上.
  // 这里在每次路由切换前强制隐藏一次, 作为兜底; 回到入库页时监控组件 play 会自己重新显示.
  try {
    if (window.WebVideoCtrl) {
      // [2026-07-16] 优先用 I_HidPluginForced (绕过焦点守卫的原始方法), 彻底保证切页必隐藏.
      //   原来直接调 I_HidPlugin, 它已被组件 guardSdkWindowMethods 劫持: 浏览器失焦时劫持函数
      //   return Promise.resolve() 跳过 native hide → 浮窗残留挡住别的页面表单(有几率发生).
      //   I_HidPluginForced 由组件在 onPluginReady 时挂上, 指向劫持前的原始 native 方法.
      const forced = window.WebVideoCtrl.I_HidPluginForced
      const fn = (typeof forced === 'function') ? forced : window.WebVideoCtrl.I_HidPlugin
      if (typeof fn === 'function') {
        // [2026-06-02] I_HidPlugin 返回 Promise; 内部 executor 同步访问 null.JS_HideWnd 会 reject.
        //   同步 try/catch 不能抓 Promise rejection — 没 .catch 时会冒到全局 unhandledrejection,
        //   表现为质价公式 / 设置 / 客商 等没初始化预览的页面进入时控制台一直报错.
        const p = fn.call(window.WebVideoCtrl)
        if (p && typeof p.catch === 'function') p.catch(() => {})
        // [2026-05-29] 隐藏海康原生窗口会让 Windows 把前台焦点交给 z 序下一个顶层窗口
        // (多开浏览器时常是另一个窗口), 表现为"点菜单画面跳到别的窗口".
        // 在用户点击的同一调用栈里立刻把本窗口重新置前, 抵消这个焦点转移.
        try { window.focus() } catch (e2) {}
      }
    }
  } catch (e) { /* 插件未加载/未预览时忽略 */ }

  const token = localStorage.getItem('token')

  // 登录页: 已登录用户访问则弹回工作台
  //   正常到达登录页只剩两条路——手动退出 / token 超时硬登出, 两者都是先清 token 再跳转,
  //   走到这里还带着 token 说明登录态有效, 直接进业务页。
  //   例外: forceChangePwd (首次登录强制改密) 的弹窗挂在登录页上, 必须放行停留。
  if (to.path === '/login') {
    if (token && store.state.forceChangePwd !== true) {
      next(store.getters.firstAccessibleRoute || '/entry-v1')
      return
    }
    next()
    return
  }

  // 未登录 -> 跳登录
  if (!token) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  // T-007: 若 store 标记 forceChangePwd, 用户尚未完成首次改密
  if (store.state.forceChangePwd === true) {
    next('/login')
    return
  }

  // T-010A: 刷新场景重拉权限
  await ensurePermissionsLoaded()

  const name = to.name
  const keys = store.state.permKeys || []

  // T-010A: 路由权限拦截
  if (name && !PUBLIC_ROUTES.has(name) && ROUTE_PERM_MAP[name]) {
    if (!keys.includes(ROUTE_PERM_MAP[name])) {
      // 无权访问 -> 改用 firstAccessibleRoute 避免递归
      const first = store.getters.firstAccessibleRoute
      if (first && first !== to.path) {
        // eslint-disable-next-line no-console
        console.warn('[router] 无权访问 ' + to.path + ', 重定向到 ' + first)
        next(first)
        return
      }
      // 完全没有任何业务菜单的权限 - 让用户能看到当前页面 (个人中心 / 通知), 不再清 token
      // 原先这里"清 token 跳 /login?noPerm=1"会和 Login.vue 后续 push 形成循环
      // eslint-disable-next-line no-console
      console.warn('[router] 用户 permKeys 为空或无任何业务菜单权限, 放行到 /profile')
      if (to.path !== '/profile') {
        next('/profile')
        return
      }
    }
  }

  // [模块开关] 烘干厂分配页 — 仅 SUPPER_ADMIN 可访问, 非超管重定向
  if (to.meta && to.meta.requireSuperAdmin) {
    const lu = store.state.loginUser || {}
    const codes = Array.isArray(lu.roleCodes) ? lu.roleCodes : []
    if (!codes.includes('SUPPER_ADMIN')) {
      console.warn('[router] 非超管访问 ' + to.path + ', 重定向到首页')
      const first = store.getters.firstAccessibleRoute
      next(first || '/profile')
      return
    }
  }

  next()
})

// [2026-05-29] 多页签: 每次导航后把目标路由登记成一个页签 (登录页/无名路由跳过).
// 组件名从已解析的路由组件取 (keep-alive 按组件名缓存); 取不到则该页不缓存.
router.afterEach((to) => {
  if (!to || !to.name || to.path === '/login') return
  let compName = ''
  try {
    const matched = to.matched && to.matched[to.matched.length - 1]
    const comp = matched && matched.components && matched.components.default
    compName = (comp && comp.name) || ''
  } catch (e) { compName = '' }
  tabsStore.addTab({
    routeName: to.name,
    title: (to.meta && to.meta.title) || to.name,
    path: to.fullPath,
    compName,
  })
})

export default router
